// API client for the Document Ingest Module.

import { pbAuthedFetch as authedFetch } from '../authHeaders';

interface UploadErrorOptions {
  code?: string;
  status?: number;
  detail?: string;
}

// Thrown by uploadFiles() for every failure case below. `.code` is a stable
// string the UI can branch on (e.g. to decide a request is retryable);
// `.message` is always the human sentence to show as-is. `.status` is the
// HTTP status when there was one (absent for a network failure).
export class UploadError extends Error {
  code?: string;
  status?: number;
  detail?: string;

  constructor(message: string, { code, status, detail }: UploadErrorOptions = {}) {
    super(message);
    this.name = 'UploadError';
    this.code = code;
    this.status = status;
    this.detail = detail;
  }
}

interface UploadResponse {
  results: Array<{ status: string; filename?: string; jobId?: string; detail?: string }>;
}

// Upload one or more files to the ingest pipeline.
// files: File[] (from <input type="file"> or drag-and-drop)
// source: 'modal' | 'drag' | 'paste' | 'api'
//
// Every failure path here was reproduced against production, not guessed:
//   - 413 file_too_large (over the 50MB limit).
//   - 400 blocked_file_type, with a `detail` naming the extension.
//   - 429, the 60-uploads/min rate limit.
//   - a bare 403 HTML page with NO JSON body at all — the AWS WAF in front of
//     the ALB blocks the request when the file body contains `<?xml`, a
//     `<!DOCTYPE ... SYSTEM ...>`, or `<script>`. Every XML upload hits this
//     in production even though the backend's own allowlist accepts XML, so
//     `res.json()` on that response throws a SyntaxError the old code caught
//     and flattened into a useless "Upload failed" — the content-type is
//     checked below before ever calling res.json().
//   - a dropped connection, which fetch() itself rejects on rather than
//     resolving with a Response — no res.ok to inspect at all.
export async function uploadFiles(files: File[], source: string = 'modal'): Promise<UploadResponse> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  formData.append('source', source);

  let res: Response;
  try {
    res = await authedFetch('/api/ingest/upload', {
      method: 'POST',
      body: formData,
    });
  } catch {
    throw new UploadError(
      'The upload was interrupted — the connection dropped before it finished. Check your connection and try again.',
      { code: 'network_error' },
    );
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = /application\/json/i.test(contentType);

  if (!res.ok) {
    // The WAF returns a bare nginx-style 403 with no JSON body at all — that
    // combination (403 + not JSON) is the signature, since every real
    // application error this route raises is JSON.
    if (res.status === 403 && !isJson) {
      throw new UploadError(
        'This file was rejected by the network firewall because its contents look like markup or script (an XML declaration, a DOCTYPE, or a <script> tag). Try converting it to a PDF and uploading that instead.',
        { code: 'waf_blocked', status: 403 },
      );
    }
    if (res.status === 413) {
      throw new UploadError('That file is over the 50MB limit.', { code: 'file_too_large', status: 413 });
    }
    if (res.status === 429) {
      throw new UploadError(
        'Too many uploads at once — this account is limited to 60 uploads per minute. Wait a moment and try again.',
        { code: 'rate_limited', status: 429 },
      );
    }
    if (isJson) {
      const body = await res.json().catch(() => ({})) as Record<string, string | undefined>;
      if (body.error === 'blocked_file_type') {
        throw new UploadError(
          body.detail || 'That file type is not accepted.',
          { code: 'blocked_file_type', status: res.status, detail: body.detail },
        );
      }
      throw new UploadError(
        body.detail || body.error || 'Upload failed.',
        { code: body.error || 'upload_failed', status: res.status, detail: body.detail },
      );
    }
    // Some other non-JSON error page (a different WAF rule, a proxy timeout
    // page, …). Say what is known — the status — rather than inventing a cause.
    throw new UploadError(`Upload failed (server responded ${res.status}).`, { code: 'unknown', status: res.status });
  }

  if (!isJson) {
    // A 200 that is not JSON should not happen, but do not assume it is safe
    // to parse — surface it instead of letting res.json() throw an opaque
    // SyntaxError the caller has to decode.
    throw new UploadError('Upload returned an unexpected response from the server.', { code: 'bad_response', status: res.status });
  }
  return res.json();
}

interface FetchQueueOptions {
  status?: string | string[];
  limit?: number;
  offset?: number;
}

// Fetch the ingest queue (list of jobs).
export async function fetchQueue({ status, limit, offset }: FetchQueueOptions = {}): Promise<unknown> {
  const params = new URLSearchParams();
  if (status) params.set('status', Array.isArray(status) ? status.join(',') : status);
  if (limit) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  const res = await authedFetch(`/api/ingest/queue?${params}`);
  if (!res.ok) throw new Error('Failed to fetch queue');
  return res.json();
}

// Fetch the review count (for navbar badge).
export async function fetchReviewCount(): Promise<{ count: number }> {
  const res = await authedFetch('/api/ingest/review-count');
  if (!res.ok) return { count: 0 };
  return res.json();
}

// Fetch a single job by ID.
export async function fetchJob(jobId: string): Promise<unknown> {
  const res = await authedFetch(`/api/ingest/jobs/${jobId}`);
  if (!res.ok) throw new Error('Job not found');
  return res.json();
}

// Fetch the mutation plan for a job. Returns { plan, items } — plan is null
// until the worker has built one, and stays null for documents that were
// indexed rather than extracted.
export async function fetchPlan(jobId: string): Promise<{ plan: unknown | null; items: unknown[] }> {
  const res = await authedFetch(`/api/ingest/jobs/${jobId}/plan`);
  if (!res.ok) return { plan: null, items: [] };
  return res.json();
}

// Fetch the global audit trail (append-only ingest events, newest first).
export async function fetchAudit({ limit = 12 }: { limit?: number } = {}): Promise<{ events: unknown[] }> {
  const res = await authedFetch(`/api/ingest/audit?limit=${limit}`);
  if (!res.ok) return { events: [] };
  return res.json();
}

interface ItemDecision {
  itemId: string;
  status: 'accepted' | 'edited' | 'rejected';
  editedValue?: string;
}

interface ApprovePlanOptions {
  itemDecisions: ItemDecision[];
  secondConfirmation?: boolean;
  note?: string | null;
}

// Approve a plan, item by item. decisions: [{ itemId, status, editedValue? }]
// where status is 'accepted' | 'edited' | 'rejected'. Critical-risk plans need
// secondConfirmation: true — the server 400s without it.
export async function approvePlan(
  jobId: string,
  { itemDecisions, secondConfirmation = false, note = null }: ApprovePlanOptions,
): Promise<Record<string, unknown>> {
  const res = await authedFetch(`/api/ingest/jobs/${jobId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemDecisions, secondConfirmation, note }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const err = new Error((body.detail as string) || (body.error as string) || 'Approval failed');
    (err as Error & { code?: string }).code = body.error as string;
    throw err;
  }
  return body;
}

// Retry a failed or blocked job.
export async function retryJob(jobId: string): Promise<unknown> {
  const res = await authedFetch(`/api/ingest/jobs/${jobId}/retry`, { method: 'POST' });
  if (!res.ok) throw new Error('Retry failed');
  return res.json();
}

// Dismiss/cancel a job.
export async function dismissJob(jobId: string): Promise<unknown> {
  const res = await authedFetch(`/api/ingest/jobs/${jobId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Dismiss failed');
  return res.json();
}
